// Eine Schrittzeile im Editor: Nummer, Symbol, Titel, Kurzsatz; Klick klappt die Eingabefelder darunter auf.
import type { Schritt } from "../api/workflows";
import { Icon } from "../components/Icon";
import { useMeta } from "../daten/meta";
import { schrittInfo, schrittProblem } from "./hilfen";
import { SchrittForm, schrittZusammenfassung } from "./SchrittForm";

interface Props {
  nummer: number;
  schritt: Schritt;
  offen: boolean;
  nurLesen: boolean;
  erster: boolean;
  letzter: boolean;
  onOffen: (offen: boolean) => void;
  onChange: (s: Schritt) => void;
  onVerschieben: (richtung: -1 | 1) => void;
  onDuplizieren: () => void;
  onEntfernen: () => void;
}

export function SchrittZeile({ nummer, schritt, offen, nurLesen, erster, letzter, onOffen, onChange, onVerschieben, onDuplizieren, onEntfernen }: Props) {
  const meta = useMeta();
  const info = schrittInfo(schritt.art);
  const problem = schrittProblem(schritt);
  const kurz = schrittZusammenfassung(
    schritt,
    (id) => meta.nutzerName(id),
    (id) => meta.status(id)?.label,
    (id) => meta.feld(id)?.name,
  );
  const stopp = (f: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    f();
  };

  return (
    <li className={`wf-schritt${offen ? " wf-schritt--offen" : ""}${problem ? " wf-schritt--problem" : ""}`} data-schritt={schritt.id}>
      <div
        className="wf-schritt__kopf"
        role="button"
        tabIndex={0}
        aria-expanded={offen}
        onClick={() => onOffen(!offen)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onOffen(!offen);
          }
        }}
      >
        <span className="wf-schritt__nr">{nummer}</span>
        <span className={`wf-schritt__symbol wf-schritt__symbol--${schritt.art}`}>
          <Icon name={info.icon} groesse={14} />
        </span>
        <span className="wf-schritt__titel">{schritt.name || info.label}</span>
        <span className="wf-schritt__kurz leise">{kurz}</span>
        {problem ? (
          <span className="wf-schritt__warnung" title={problem}>
            <Icon name="warnung" groesse={14} />
            <span className="wf-schritt__warnung-text">{problem}</span>
          </span>
        ) : null}
        {!nurLesen ? (
          <span className="wf-schritt__werkzeuge">
            <button type="button" className="symbolknopf symbolknopf--klein" title="Nach oben" aria-label="Schritt nach oben" disabled={erster} onClick={stopp(() => onVerschieben(-1))}>
              <Icon name="pfeil-hoch" groesse={13} />
            </button>
            <button type="button" className="symbolknopf symbolknopf--klein" title="Nach unten" aria-label="Schritt nach unten" disabled={letzter} onClick={stopp(() => onVerschieben(1))}>
              <Icon name="pfeil-runter" groesse={13} />
            </button>
            <button type="button" className="symbolknopf symbolknopf--klein" title="Duplizieren" aria-label="Schritt duplizieren" onClick={stopp(onDuplizieren)}>
              <Icon name="kopieren" groesse={13} />
            </button>
            <button type="button" className="symbolknopf symbolknopf--klein" title="Schritt entfernen" aria-label="Schritt entfernen" onClick={stopp(onEntfernen)}>
              <Icon name="papierkorb" groesse={13} />
            </button>
          </span>
        ) : null}
        <Icon name={offen ? "chevron-hoch" : "chevron-unten"} groesse={14} className="wf-schritt__pfeil" />
      </div>
      {offen ? (
        <div className="wf-schritt__inhalt">
          <fieldset disabled={nurLesen} className="wf-fieldset">
            <div className="feld wf-feld wf-feld--breit">
              <span className="feld__label">Bezeichnung (optional)</span>
              <input className="eingabe" value={schritt.name ?? ""} placeholder={info.label} onChange={(e) => onChange({ ...schritt, name: e.target.value || null })} />
            </div>
            <SchrittForm schritt={schritt} onChange={(config) => onChange({ ...schritt, config })} />
          </fieldset>
        </div>
      ) : null}
    </li>
  );
}
